import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, MapPin, Phone, Mail, Download, ArrowLeft, Clock, Info } from 'lucide-react';
import { getMosque, getTodayPrayerTimes } from '../api';

const BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';

const PRAYER_KEYS = [
  { key: 'fajr', label: 'Fajr', icon: '🌙' },
  { key: 'sunrise', label: 'Sunrise', icon: '🌅' },
  { key: 'dhuhr', label: 'Dhuhr', icon: '☀️' },
  { key: 'asr', label: 'Asr', icon: '🌤' },
  { key: 'maghrib', label: 'Maghrib', icon: '🌇' },
  { key: 'isha', label: 'Isha', icon: '🌃' },
];

export default function MosqueDetail() {
  const { id } = useParams();
  const [mosque, setMosque] = useState(null);
  const [todayTimes, setTodayTimes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([getMosque(id), getTodayPrayerTimes()])
      .then(([mosqueRes, timesRes]) => {
        setMosque(mosqueRes.data);
        const myTimes = timesRes.data.find(
          (t) => t.mosqueId?._id === id || t.mosqueId === id
        );
        setTodayTimes(myTimes || null);
      })
      .catch(() => setError('Failed to load mosque details.'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-20 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !mosque) {
    return (
      <div className="min-h-screen pt-24 pb-20 flex flex-col items-center justify-center text-center px-4">
        <Building2 className="w-16 h-16 text-gray-600 mb-4" />
        <h2 className="text-2xl font-bold text-white mb-2">Mosque Not Found</h2>
        <p className="text-gray-400 mb-6">{error || 'The mosque you are looking for does not exist.'}</p>
        <Link to="/mosques" className="btn-primary px-6 py-2 rounded-xl flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back to Mosques
        </Link>
      </div>
    );
  }

  const today = new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="min-h-screen pt-24 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-8"
        >
          <Link
            to="/mosques"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-emerald-400 transition-colors group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back to All Mosques
          </Link>
        </motion.div>

        {/* Mosque Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card rounded-2xl p-8 mb-8"
        >
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center flex-shrink-0 glow-emerald">
              <Building2 className="w-10 h-10 text-white" />
            </div>
            <div className="flex-1">
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">{mosque.name}</h1>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {mosque.address && (
                  <div className="flex items-start gap-3 text-gray-300">
                    <MapPin className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{mosque.address}</span>
                  </div>
                )}
                {mosque.phone && (
                  <div className="flex items-center gap-3 text-gray-300">
                    <Phone className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <a href={`tel:${mosque.phone}`} className="text-sm hover:text-emerald-400 transition-colors">
                      {mosque.phone}
                    </a>
                  </div>
                )}
                {mosque.email && (
                  <div className="flex items-center gap-3 text-gray-300">
                    <Mail className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <a href={`mailto:${mosque.email}`} className="text-sm hover:text-emerald-400 transition-colors truncate">
                      {mosque.email}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="mt-6 flex flex-wrap gap-3">
            {mosque.timetablePdfUrl && (
              <a
                href={`${BASE_URL}${mosque.timetablePdfUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download PDF Timetable
              </a>
            )}
            <Link
              to="/timetables"
              className="glass-card-light px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-300 flex items-center gap-2 hover:text-emerald-400 transition-colors"
            >
              <Clock className="w-4 h-4" />
              View Timetable Image
            </Link>
          </div>
        </motion.div>

        {/* About */}
        {mosque.about && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card rounded-2xl p-6 mb-8"
          >
            <div className="flex items-center gap-2 mb-4">
              <Info className="w-5 h-5 text-emerald-400" />
              <h2 className="text-lg font-semibold text-white">About</h2>
            </div>
            <p className="text-gray-400 leading-relaxed">{mosque.about}</p>
          </motion.div>
        )}

        {/* Today's Prayer Times */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-white">Today's Prayer Times</h2>
            <span className="badge-emerald">{today}</span>
          </div>

          {todayTimes ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {PRAYER_KEYS.map(({ key, label, icon }) => (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.05 }}
                  className="prayer-card rounded-xl p-5 text-center"
                >
                  <div className="text-2xl mb-2">{icon}</div>
                  <div className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-1">{label}</div>
                  <div className="text-white font-bold text-xl">
                    {todayTimes[key] || <span className="text-gray-600 text-base">N/A</span>}
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="glass-card rounded-2xl flex flex-col items-center justify-center py-12 text-center">
              <Clock className="w-12 h-12 text-gray-600 mb-3" />
              <p className="text-gray-400">No prayer times available for today.</p>
              <p className="text-gray-500 text-sm mt-1">Please contact the mosque directly.</p>
            </div>
          )}
        </motion.div>

        {/* Timetable Image Preview */}
        {mosque.timetableImageUrl && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-8 glass-card rounded-2xl overflow-hidden"
          >
            <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Monthly Timetable</h2>
              <Link to="/timetables" className="text-emerald-400 text-sm hover:text-emerald-300 transition-colors">
                View Full
              </Link>
            </div>
            <div className="p-4">
              <img
                src={`${BASE_URL}${mosque.timetableImageUrl}`}
                alt="Monthly timetable"
                className="w-full h-auto rounded-lg max-h-80 object-cover"
              />
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
